import { COMPLIANCE_STATUSES, STEP_TYPE_STATUSES, CERTIFICATE_STATUSES } from 'constants/status.constants';
import { EDUCATION_TYPES } from 'constants/education-step.constants';
import _get from 'lodash-es/get';
import _size from 'lodash-es/size';
import { STEP_TYPES } from 'constants/status.constants';
import { buildFormData } from 'utils/formdata-builder';
import { NoContentConfig } from 'components/no-content/no-content.component';
import { geCorrectUnitsFromMilliseconds } from 'features/professional-step/education/education-step.helpers';
import { SHARE_CE_UNITS } from 'constants/constants';

interface SubmitDataEntry {
  compliance_status: COMPLIANCE_STATUSES;
  hours: number;
  units?: number;
}

interface RequirementForSubmit {
  submitData: SubmitDataEntry[];
  max: number;
  min: number;
  subrequirements?: any[];
}

interface DataForOpportunityUpload {
  opportunity: OpportunityInfoForUpload;
  files: File[];
  stepId: number;
  unitsAmount: number;
  requirementId?: number;
  ce_type_id: number;
  shareCeUnits: SHARE_CE_UNITS;
}

interface OpportunityInfoForUpload {
  id: number;
  name: string;
  provider_name: string;
  provider_id: number;
  hours: number;
}
export function isRequirementPendingVerification(advancedOptionsAllowed: boolean, requirement): boolean {
  if (!requirement) {
    return null;
  }

  const submittedHours = getSubmittedUnits(requirement.submit_data);
  return advancedOptionsAllowed ? submittedHours >= requirement.max : submittedHours >= requirement.min;
}

export function getUnitsRequiredLeft(
  advancedOptionsAllowed: boolean,
  requirement: RequirementForSubmit,
  stepData?: { submitData: SubmitDataEntry[]; max: number },
): number {
  const requirementSubmitData = _get(requirement, 'submitData');
  const submitRequirementDataExists = !!requirementSubmitData && Array.isArray(requirementSubmitData);

  if (!advancedOptionsAllowed && !submitRequirementDataExists) {
    return null;
  }

  const submitData = submitRequirementDataExists ? requirement.submitData : stepData.submitData;

  const hasSubrequirements = _size(requirement.subrequirements);
  const submittedUnits =
    advancedOptionsAllowed && hasSubrequirements && requirementSubmitData
      ? getTotalSubrequirementsSubmitData(requirement)
      : getSubmittedUnits(submitData);

  if (advancedOptionsAllowed) {
    const max = submitRequirementDataExists ? requirement.max : stepData.max;
    return +(max - submittedUnits).toFixed(2);
  }

  return +(requirement.min - submittedUnits).toFixed(2);
}

export function getSubmittedUnits(submitData: SubmitDataEntry[]): number {
  return submitData
    .filter(submitEntry => submitEntry.compliance_status !== COMPLIANCE_STATUSES.REJECTED)
    .reduce((sum, sd) => {
      return +(sum + sd.units).toFixed(2);
    }, 0);
}

export function getHoursCompleted(requirement: any, advancedOptionsAllowed: boolean): number {
  const totalCompletedHours = getSubmittedUnits(requirement.submit_data);
  const isRequirementHoursOverloaded = advancedOptionsAllowed && totalCompletedHours > requirement.max;

  return isRequirementHoursOverloaded ? requirement.max : +totalCompletedHours.toFixed(2);
}

export function needToShowMinRequiredMessage(data: { unitsForSubmit: number; unitsLeft: number }) {
  return data.unitsForSubmit > data.unitsLeft;
}

export function getParentRequirement(parentRequirementId, stepRequirements) {
  return stepRequirements.find(requirement => requirement.id === parentRequirementId);
}

export function getRequiredUnitsMessage(advancedOptionsAllowed: boolean, unitName: string): string {
  return advancedOptionsAllowed
    ? `Please note, that any CE ${unitName} beyond the maximum required will not be accounted for`
    : `Please note, that only min required ${unitName} will be accounted`;
}

export function getTotalSubrequirementsSubmitData(requirement) {
  return requirement.subrequirements.reduce((sum, subrequirement) => {
    return +(sum + getSubmittedUnits(subrequirement.submit_data)).toFixed(2);
  }, 0);
}

export function getDataForOpportunityUpload(data: DataForOpportunityUpload): { formData: any; fullData: any } {
  const { files, stepId, unitsAmount, opportunity, requirementId, ce_type_id, shareCeUnits } = data;
  const baseInfo = {
    files,
    id: stepId,
  };

  const submittedUnits = {
    hours: unitsAmount,
    units: unitsAmount,
  };

  return {
    formData: buildFormData(
      {
        ...baseInfo,
        ...submittedUnits,
        ...(requirementId ? { requirement_id: requirementId } : {}),
        option_id: opportunity.id,
        provider_id: opportunity.provider_id,
      },
      'files',
    ),
    fullData: {
      ...baseInfo,
      values: {
        ...submittedUnits,
        selectedOpportunityHours: opportunity.hours,
        title: opportunity.name,
        provider: opportunity.provider_name,
        courseId: opportunity.id,
        ...(requirementId ? { requirement_id: requirementId } : {}),
      },
      ce_type_id,
      isOpportunity: true,
      shareCeUnits,
    },
  };
}

export function getCompletedPlaceholderConfig(title: string): NoContentConfig {
  return {
    src: '/assets/images/professional/icon-completed/completed.png',
    srcset: `
            /assets/images/professional/icon-completed/completed.png 1x,
            /assets/images/professional/icon-completed/completed@2x.png 2x,
            /assets/images/professional/icon-completed/completed@3x.png 3x
        `,
    title: `${title}`,
  };
}

export function getArchivedPlaceholderConfig(title: string): NoContentConfig {
  return {
    src: '/assets/images/common/status/archived.svg',
    title: `${title}`,
  };
}

export function getPendingVerificationPlaceholderConfig(unitName: string): NoContentConfig {
  return {
    iconName: 'log-step',
    title: `Pending verification. You have already submitted all required ${unitName}.`,
  };
}

export function getRequirementPendingVerificationPlaceholderConfig(unitName: string): NoContentConfig {
  return {
    iconName: 'log-step',
    title: `You have already submitted all required ${unitName}.`,
  };
}

export function getUnitsAmount(units: number, isUnits: boolean): number {
  return isUnits ? units : geCorrectUnitsFromMilliseconds(units);
}

export function isRollingAuditStep(step): boolean {
  return (
    _get(step, 'certification_status') !== CERTIFICATE_STATUSES.Complete &&
    _get(step, 'step_type_id') === STEP_TYPES.EDUCATION &&
    _get(step, 'status_id') === STEP_TYPE_STATUSES.COMPLETED &&
    _get(step, 'content.details.ce_type.id') === EDUCATION_TYPES.GENERAL_CE &&
    !!(_get(step, 'content.auditStatusId') || _get(step, 'phase_to_be_audited'))
  );
}
